/**
 * Region Selector Component
 * Simple dropdown for US/UK region selection
 */

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Globe } from 'lucide-react';
import { useRegional } from '../../context/RegionalContext';
import FlagIcon from '../ui/FlagIcon';

const RegionSelector = () => {
  const { region, regions, setRegion } = useRegional();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-slate-100/80 transition-colors text-slate-600 hover:text-slate-900"
        aria-label="Select region"
        data-testid="region-selector"
      >
        <FlagIcon countryCode={region.code} className="w-5 h-3.5 rounded-sm shadow-sm" />
        <span className="text-sm font-medium hidden sm:inline">{region.currency}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div 
          className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200"
        >
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 flex items-center gap-2">
            <Globe className="w-4 h-4 text-slate-400" />
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
              Select Region
            </p>
          </div>
          
          <div className="py-1">
            {Object.values(regions).map((r) => (
              <button
                key={r.code}
                onClick={() => {
                  setRegion(r.code);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-3 hover:bg-slate-50 transition-colors ${
                  region.code === r.code ? 'bg-blue-50' : ''
                }`}
                data-testid={`region-option-${r.code}`}
              >
                <FlagIcon countryCode={r.code} className="w-7 h-5 rounded-sm shadow-sm" />
                <div className="flex-1 text-left">
                  <p className={`text-sm font-medium ${region.code === r.code ? 'text-blue-600' : 'text-slate-900'}`}>
                    {r.name}
                  </p>
                  <p className="text-xs text-slate-500">
                    {r.currency} ({r.symbol})
                  </p>
                </div>
                {region.code === r.code && (
                  <Check className="w-4 h-4 text-blue-600" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RegionSelector;
